import {
  AUDIT_LOG_ACTOR_FALLBACK,
  AUDIT_LOG_CATEGORY_CONFIG,
  AUDIT_LOG_DEFAULT_ACTOR,
  AUDIT_LOG_ICON,
  AUDIT_LOG_ICON_COLOR,
  AUDIT_LOG_LEAD_ACTOR_TYPES,
  AUDIT_LOG_TONE,
  AUDIT_LOG_TONE_COLOR,
} from "./constants";

/**
 * Turns one `getLeadAuditLog` row into the `{ icon, iconColor, title,
 * metaPrefix, timestamp }` shape `AuditLogItem` renders.
 *
 * The row carries no category of its own, so when the drawer is on "All" the
 * category is inferred from `changedFields` against
 * `AUDIT_LOG_CATEGORY_CONFIG[*].fields`. When a chip is active the server has
 * already narrowed the result set, so that category is used directly and the
 * inference only has to pick the changed field within it.
 *
 * Every step degrades rather than throws: an unmapped field, value or category
 * still produces a readable row.
 *
 * Accepts both shapes the audit payload arrives in: the camelCase GraphQL row
 * from `getLeadAuditLog`, and the snake_case JSON embedded in a CONSENT_UPDATE
 * message's `content`. Both describe the same audit record (the message carries
 * its `audit_log_id`), so one mapper keeps the drawer and the thread in step.
 */

/** Reads a key from either casing, so one mapper serves both payload shapes. */
const pick = (entry, camel, snake) => entry?.[camel] ?? entry?.[snake];

/** `oldValues`/`newValues` arrive as JSON — sometimes stringified. */
const parseValues = raw => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/** `changedFields` may be an array or a comma-separated string. */
const parseChangedFields = raw => {
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string" && raw.trim()) {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      } catch {
        /* fall through to the comma split */
      }
    }
    return trimmed
      .split(",")
      .map(f => f.trim())
      .filter(Boolean);
  }
  return [];
};

/** `emailConsent` and `email_consent` must hit the same config entry. */
const normaliseField = field =>
  String(field ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();

/**
 * Value → lookup key. Booleans and the string forms the API may send for them
 * collapse onto TRUE/FALSE; null/undefined onto NULL. Everything else uppercases
 * so "enrolled" and "ENROLLED" match the same entry.
 */
const valueKey = value => {
  if (value === null || value === undefined || value === "") return "NULL";
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  const token = String(value).trim().toUpperCase();
  if (token === "TRUE" || token === "1") return "TRUE";
  if (token === "FALSE" || token === "0") return "FALSE";
  return token;
};

/**
 * "campaign_auto_response" → "Campaign auto response" — the last-resort label.
 * Sentence-cased rather than left as-is so an unmapped stored value ("ENROLLED")
 * reads as prose instead of shouting.
 */
const humanise = token =>
  String(token ?? "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/^./, c => c.toUpperCase());

/** First config whose `fields` list contains one of the row's changed fields. */
const inferCategory = fields => {
  const normalised = fields.map(normaliseField);
  return (
    Object.keys(AUDIT_LOG_CATEGORY_CONFIG).find(category =>
      AUDIT_LOG_CATEGORY_CONFIG[category].fields.some(f => normalised.includes(normaliseField(f)))
    ) || null
  );
};

/**
 * The field this row is about: the first changed field the category claims,
 * falling back to the first changed field at all, then to the first key present
 * in `newValues` (some writers leave `changedFields` empty).
 */
const pickField = (fields, config, newValues) => {
  if (config) {
    const owned = fields.find(f =>
      config.fields.some(cf => normaliseField(cf) === normaliseField(f))
    );
    if (owned) return owned;
  }
  return fields[0] || Object.keys(newValues)[0] || null;
};

/**
 * Reads a field out of a values object tolerating either casing, since the row
 * may report `changedFields: ["emailConsent"]` against `{ email_consent: ... }`.
 */
const readValue = (values, field) => {
  if (!field) return undefined;
  if (field in values) return values[field];
  const target = normaliseField(field);
  const match = Object.keys(values).find(k => normaliseField(k) === target);
  return match === undefined ? undefined : values[match];
};

/**
 * Who to name for a change, per the EAI-1807 attribution rules: a lead's own
 * action is credited to the lead by name, an employee's to the username on the
 * row, and anything the system did to "EAI" — unless the row names the external
 * system that made the change, which is more informative than "EAI".
 */
const resolveActor = (updatedByType, updatedByName, leadName) => {
  if (AUDIT_LOG_LEAD_ACTOR_TYPES.includes(updatedByType)) {
    return leadName || updatedByName || AUDIT_LOG_ACTOR_FALLBACK.LEAD;
  }
  return updatedByName || AUDIT_LOG_ACTOR_FALLBACK[updatedByType] || AUDIT_LOG_DEFAULT_ACTOR;
};

/**
 * @param {object} entry raw `getLeadAuditLog.data[n]`
 * @param {string} [activeCategory] the selected chip, when one is active
 * @param {string} [leadName] the lead this log belongs to, named on their own
 *   actions (an inbound START/STOP reply is "by {lead}", not "by Customer")
 */
const mapAuditLogEntry = (entry, activeCategory, leadName) => {
  const newValues = parseValues(pick(entry, "newValues", "new_values"));
  const changedFields = parseChangedFields(pick(entry, "changedFields", "changed_fields"));

  const category = inferCategory(changedFields) || activeCategory || null;
  const config = category ? AUDIT_LOG_CATEGORY_CONFIG[category] : null;

  const field = pickField(changedFields, config, newValues);
  const rawValue = readValue(newValues, field);
  // A stored null is a state ("SMS consent reset", "implied consent"); a field
  // missing from `newValues` altogether is not, and must not borrow that
  // sentence — `undefined` only ever means "the row did not report a value".
  const hasValue = rawValue !== undefined;
  const mappedValue = hasValue ? config?.values?.[valueKey(rawValue)] : undefined;

  // With no config match the row still says what changed and to what, e.g.
  // "Campaign auto response changed to ENROLLED".
  // normaliseField first so a camelCase field name ("campaignAutoResponse")
  // still splits into words rather than rendering as one run-on token.
  const noun = config?.noun || humanise(normaliseField(field)) || "Consent";
  const verb = config?.verb || "changed to";
  const label = mappedValue?.label ?? (hasValue ? humanise(valueKey(rawValue)) : null);
  const tone = mappedValue?.tone || AUDIT_LOG_TONE.NEUTRAL;

  const updatedByName = pick(entry, "updatedByName", "updated_by_name");
  const updatedByType = String(pick(entry, "updatedByType", "updated_by_type") ?? "").toUpperCase();

  return {
    id: entry?.id ?? entry?.audit_log_id,
    category,
    updatedByName,
    updatedByType,
    icon: config?.icon || AUDIT_LOG_ICON.OTTO,
    iconColor: AUDIT_LOG_TONE_COLOR[tone] || AUDIT_LOG_ICON_COLOR.GREY,
    // A value may supply the whole sentence when "{noun} {verb} {label}" does
    // not fit it (e.g. a pending SMS consent request).
    title: mappedValue?.title || (label ? `${noun} ${verb} ${label}` : noun),
    metaPrefix: resolveActor(updatedByType, updatedByName, leadName),
    timestamp: pick(entry, "createdAt", "created_at"),
  };
};

export default mapAuditLogEntry;
